<script setup lang="ts">
import ControlCurrentRoute from '@/components/shared/admin/reports/ControlCurrentRoute.vue'
import Graphics from '@/components/shared/admin/reports/Graphics.vue'
import TotalCard from '@/components/shared/admin/reports/TotalCard.vue'
import WorkshopSelector from '@/components/shared/admin/reports/WorkshopSelector.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Title } from '@/components/ui/title'
import { useDownloadReport } from '@/composables/useDownloadReport'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head } from '@inertiajs/vue3'
import { useUrlSearchParams } from '@vueuse/core'
import { FileText, Loader2 } from 'lucide-vue-next'

interface Report {
    name: string
    total: number
}

interface Workshop {
    id: number
    description: string
    start: string
}

interface CurrentWorkshop {
    id: number
    description: string
}

const props = defineProps<{
    workshops: Workshop[]
    current_workshop: CurrentWorkshop | null
    total?: Report[]
    ticket_generated?: Report[]
    invited?: Report[]
    to_distribute?: Report[]
    pending_payment?: Report[]
}>()

const colors = ['#2DD4BF', '#1D4ED8', '#EC4899', '#FDBA74']

const currentRoute = 'tickets'
const { download_pdf: downloadPdf } = useUrlSearchParams()
const { handleDownload, isGenerating } = useDownloadReport('tickets', props.current_workshop?.id)
</script>

<template>
    <Head title="Relatórios" />
    <div class="bg-slate-50">
        <LightLayout>
            <div class="container">
                <Title v-if="!downloadPdf">Relatórios</Title>
                <div v-if="!downloadPdf" class="mt-4 flex items-center justify-between gap-4">
                    <div class="flex items-center gap-4">
                        <ControlCurrentRoute :current-route="currentRoute" :current-workshop-id="current_workshop?.id ?? null" />
                        <WorkshopSelector :workshops="workshops" :current-workshop-id="current_workshop?.id ?? null" />
                    </div>

                    <div v-if="current_workshop">
                        <Button variant="outline" class="gap-5 rounded-md bg-white" @click="handleDownload" :disabled="isGenerating">
                            <template v-if="!isGenerating"> PDF <FileText class="text-slate-400" /> </template>
                            <template v-else>
                                <Loader2 class="animate-spin" />
                            </template>
                        </Button>
                    </div>
                </div>

                <template v-if="current_workshop">
                    <Title class="mt-6" size="sm">Relatório de ingressos</Title>

                    <Card class="mt-4">
                        <CardContent class="grid gap-y-24 p-6 lg:grid-cols-2">
                            <div class="jus col-span-full grid items-center gap-6 px-2 lg:grid-cols-2 lg:px-8">
                                <Graphics sublabel="Ingressos" title="Total de ingressos" :data="total!" :colors="colors"> </Graphics>
                                <div class="grid items-start gap-4 md:grid-cols-2">
                                    <TotalCard
                                        v-for="item in total"
                                        :key="item.name"
                                        :title="`${item.name}`"
                                        :value="item.total"
                                        class="w-full lg:basis-full"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card class="mt-4" :class="{ 'break-page': downloadPdf }">
                        <CardContent class="grid gap-y-24 p-6 lg:grid-cols-2">
                            <div class="px-8 lg:border-r lg:border-slate-200">
                                <Graphics sublabel="Ingressos" title="Total Vinculado" :data="ticket_generated!" :colors="colors">
                                    <template #total-cards>
                                        <div class="grid items-start gap-4 sm:grid-cols-3 lg:basis-full">
                                            <TotalCard
                                                v-for="item in ticket_generated"
                                                :key="item.name"
                                                :title="`${item.name}`"
                                                :value="item.total"
                                                class="lg:basis-1/3"
                                            />
                                        </div>
                                    </template>
                                </Graphics>
                            </div>
                            <div class="px-8">
                                <Graphics sublabel="Ingressos" title="Total Convidado" :data="invited!" :colors="colors">
                                    <template #total-cards>
                                        <div class="grid items-start gap-4 sm:grid-cols-3 lg:basis-full">
                                            <TotalCard
                                                v-for="item in invited"
                                                :key="item.name"
                                                :title="`${item.name}`"
                                                :value="item.total"
                                                class="lg:basis-1/3"
                                            />
                                        </div>
                                    </template>
                                </Graphics>
                            </div>
                        </CardContent>
                    </Card>
                    <Card class="mt-4" :class="{ 'break-page': downloadPdf }">
                        <CardContent class="grid gap-y-24 p-6 lg:grid-cols-2">
                            <div class="px-8 lg:border-r lg:border-slate-200">
                                <Graphics sublabel="Ingressos" title="Total À Distribuir" :data="to_distribute!" :colors="colors">
                                    <template #total-cards>
                                        <div class="grid items-start gap-4 sm:grid-cols-3 lg:basis-full">
                                            <TotalCard
                                                v-for="item in to_distribute"
                                                :key="item.name"
                                                :title="`${item.name}`"
                                                :value="item.total"
                                                class="lg:basis-1/3"
                                            />
                                        </div>
                                    </template>
                                </Graphics>
                            </div>
                            <div class="px-8">
                                <Graphics sublabel="Ingressos" title="Total Pendentes de pagamento" :data="pending_payment!" :colors="colors">
                                    <template #total-cards>
                                        <div class="grid items-start gap-4 sm:grid-cols-3 lg:basis-full">
                                            <TotalCard
                                                v-for="item in pending_payment"
                                                :key="item.name"
                                                :title="`${item.name}`"
                                                :value="item.total"
                                                class="lg:basis-1/3"
                                            />
                                        </div>
                                    </template>
                                </Graphics>
                            </div>
                        </CardContent>
                    </Card>
                </template>

                <Card v-else class="mt-6">
                    <CardContent class="p-6 text-center text-slate-500">
                        Selecione um evento para visualizar o relatório de ingressos.
                    </CardContent>
                </Card>
            </div>
        </LightLayout>
    </div>
</template>
